import React, {Fragment, useState} from 'react'
import {useParams, useHistory, useRouteMatch, Route} from 'react-router-dom'
import {toParams, pluralise, toPagination, titlecase} from 'admin/lib/format'
import useAccounts from 'admin/hooks/useAccounts'
import useOrganisation from 'admin/hooks/useOrganisation'
import useEffect from 'admin/hooks/useEffect'
import useDocument from 'admin/hooks/useDocument'
import useBoolState from 'admin/hooks/useBoolState'
import selectableCollection from 'admin/components/hoc/selectableCollection'
import Pagination from 'admin/components/shared/Pagination'
import Dropdown from 'admin/components/shared/Dropdown'
import Modal from 'admin/components/shared/Modal'
import NewAccount from 'admin/components/NewAccount'
import Table from './Table'
import Filters from './Filters'
import EditAccounts from './Edit'
import Page from 'admin/components/shared/Page'
import Sorting from 'admin/components/shared/Sorting'
import Deletion from '../Deletion'
import ModalLink from '../shared/ModalLink'

const sortOptions = [
  {sortBy: 'name', sortDirection: 'asc', sortLabel: 'Name', ascLabel: 'A-Z', descLabel: 'Z-A'},
  {sortBy: 'customer_number', sortDirection: 'asc', sortLabel: 'Customer number'},
  {sortBy: 'num_of_alerts', sortDirection: 'desc', sortLabel: 'Alerts'},
  {sortBy: 'num_of_active_services', sortDirection: 'desc', sortLabel: 'Active services'},
  {
    sortBy: 'created_at',
    sortDirection: 'asc',
    sortLabel: 'Date created',
    ascLabel: 'Oldest first',
    descLabel: 'Latest first'
  },
  {
    sortBy: 'last_transaction_date',
    sortDirection: 'desc',
    sortLabel: 'Last transaction date',
    ascLabel: 'Oldest first',
    descLabel: 'Latest first'
  },
  {sortBy: 'balance', sortDirection: 'asc', sortLabel: 'Balance'},
  {
    sortBy: 'overdue_since',
    sortDirection: 'asc',
    sortLabel: 'Overdue date',
    ascLabel: 'Oldest first',
    descLabel: 'Latest first'
  }
]

export default () => {
  const {organisationID, state} = useParams()
  const match = useRouteMatch()
  const history = useHistory()
  const {organisation} = useOrganisation(organisationID)
  const {accounts, totalCount, filters, setFilters, destroy, activate, archive, merge, update} = useAccounts(
    organisationID,
    {state}
  )

  const [selected, setSelected] = useState([])
  const [showDeleteModal, setShowDeleteModal, toggleShowDeleteModal] = useBoolState()
  const [showEditModal, setShowEditModal, toggleShowEditModal] = useBoolState()

  useDocument({title: `${titlecase(state)} accounts`})

  useEffect(() => setSelected([]), [accounts])

  const SelectableTable = selectableCollection(Table, {
    onSelect: setSelected,
    selectedIdentifiers: selected
  })

  const handleRowClick = accountID => {
    history.push(`/orgs/${organisationID}/accounts/${accountID}`)
  }

  const handleUpdate = attributes => {
    update({accountIds: selected, ...attributes})
    toggleShowEditModal()
  }

  const BulkAction = ({action, children}) => {
    const handleClick = e => {
      e.preventDefault()
      action({accountIds: selected})
    }
    return (
      <a onClick={handleClick} className='dropdown-item'>
        {children}
      </a>
    )
  }

  return (
    <Fragment>
      <Route path={`${match.path}/new`} component={NewAccount} />

      {showDeleteModal && (
        <Deletion
          confirm={`Are you sure you want to delete ${pluralise(`account`, selected.length, true)}?`}
          onClose={toggleShowDeleteModal}
          onConfirm={({notes}) => destroy({recordIds: selected, notes})}
        />
      )}

      {showEditModal && (
        <Modal onClose={toggleShowEditModal} flashId='edit-accounts'>
          <EditAccounts
            onSave={handleUpdate}
            onCancel={toggleShowEditModal}
            accounts={accounts.filter(a => selected.includes(String(a.id)))}
          />
        </Modal>
      )}

      <Page
        id='admin_accounts'
        title={`Manage ${state} accounts`}
        subtitle={
          totalCount
            ? `Showing ${toPagination(accounts.length, totalCount, `${state} account`)}`
            : `No ${state} accounts found`
        }
        buttons={
          state === 'active' ? (
            <ModalLink modal='new-account' className='button is-light'>
              New account
            </ModalLink>
          ) : null
        }>
        <Filters {...filters} organisationID={organisationID} onSubmit={setFilters} />
        {accounts.length != 0 ? (
          <Fragment>
            {selected.length ? (
              <div className='level'>
                <div className='level-left has-background-info box mb-0 is-shadowless p-3 pl-4'>
                  <div className='level-item has-text-white'>
                    {selected.length === accounts.length ? 'All ' : ''}
                    {pluralise('account', selected.length, true)} on this page {pluralise('is', selected.length)}{' '}
                    selected
                  </div>
                  <Dropdown id='accounts-export-menu' className='level-item'>
                    <button className='button is-outlined is-small'>
                      <span>Export</span>
                      <span className='icon is-small'>
                        <i className='fa fa-caret-down' aria-hidden='true'></i>
                      </span>
                    </button>
                    <a
                      href={`/orgs/${organisationID}/downloads/accounts.csv?${toParams({
                        account_ids: selected
                      })}`}
                      className='dropdown-item'>
                      Download CSV
                    </a>
                    <a
                      href={`/orgs/${organisationID}/downloads/accounts.kml?${toParams({
                        account_ids: selected
                      })}`}
                      className='dropdown-item'>
                      Download KML
                    </a>
                  </Dropdown>
                  <Dropdown id='accounts-actions-menu' className='level-item'>
                    <button className='button is-outlined is-small'>
                      <span>Action</span>
                      <span className='icon is-small'>
                        <i className='fa fa-caret-down' aria-hidden='true'></i>
                      </span>
                    </button>
                    {organisation.smsEnabled && state === 'active' ? (
                      <ModalLink
                        modal='new-text-message'
                        params={{accountIds: selected.join(',')}}
                        className='dropdown-item'>
                        Text message
                      </ModalLink>
                    ) : null}
                    {state === 'draft' ? (
                      <a onClick={toggleShowDeleteModal} className='dropdown-item'>
                        Delete
                      </a>
                    ) : null}
                    {state === 'draft' ? (
                      <BulkAction action={activate}>Activate</BulkAction>
                    ) : state === 'active' ? (
                      <BulkAction action={archive}>Archive</BulkAction>
                    ) : (
                      <BulkAction action={activate}>Unarchive</BulkAction>
                    )}
                    {selected.length >= 2 && selected.length < 3 ? <BulkAction action={merge}>Merge</BulkAction> : null}
                  </Dropdown>
                  <a onClick={toggleShowEditModal} className='level-item button is-outlined is-small'>
                    Edit
                  </a>
                </div>
              </div>
            ) : null}
            <div className='level'>
              <div className='level-left'>
                <Sorting
                  className='level-item'
                  sortOptions={sortOptions}
                  sortBy={filters.sortBy}
                  sortDirection={filters.sortDirection}
                  onChange={setFilters}
                />
              </div>

              <div className='level-right'>
                <Pagination
                  className='level-item'
                  page={filters.page}
                  limit={filters.limit}
                  total={totalCount}
                  onChange={setFilters}
                />
              </div>
            </div>
            <SelectableTable data={accounts} organisation={organisation} onClick={handleRowClick} />
            {accounts.length >= 10 && (
              <div className='level'>
                <div className='level-left'></div>
                <div className='level-right'>
                  <Pagination
                    page={filters.page}
                    limit={filters.limit}
                    total={totalCount}
                    onChange={setFilters}
                    className='level-item'
                  />
                </div>
              </div>
            )}
          </Fragment>
        ) : null}
      </Page>
    </Fragment>
  )
}
